/**
 * V-NAV-DEVICE — is this a MOBILE DEVICE? Not "is this window narrow?".
 *
 * ⛔⛔ WHY NOT WIDTH. The vault ships two separate UIs: the desktop three-pane overlay
 * (VaultPage) and the routed mobile screens. Choosing between them by `window.innerWidth`
 * (useBreakpoint's `isMobile`, < 768px) means a DESKTOP user who narrows their browser is
 * handed a phone UI mid-session, and a resize flips the app between two different products.
 * The thing that decides which UI is right is the DEVICE, and the device does not change
 * while the page is open.
 *
 * ⭐ UA CLIENT HINTS FIRST, REGEX ONLY AS A FALLBACK. `navigator.userAgentData.mobile` is a
 * boolean the browser itself asserts about the device; it is structured, unspoofed by the
 * usual UA-string games, and it is the answer when it exists (Chromium). It does not exist on
 * Safari or Firefox, and ONLY there do we fall back to matching the UA string. A regex alone
 * would be the whole implementation on Chromium too, where a far better signal is sitting
 * right there.
 *
 * ⚠ "NOT CONFIDENTLY MOBILE" IS A REAL ANSWER, AND IT MEANS DESKTOP. Anything this cannot
 * positively identify as a phone -- an iPad (iPadOS reports a Macintosh UA and has no UA-CH),
 * an unknown tablet, a browser with a stripped UA, a server render -- is NOT given the mobile
 * UI. It gets the desktop vault, or, if it reached a mobile-only route directly, the notice
 * that offers the desktop vault. Guessing "probably a phone" is how a desktop user ends up in
 * a phone UI, which is the defect this module exists to remove.
 *
 * `iPad` is deliberately ABSENT from the pattern: a tablet is not a phone, and the graceful
 * landing for it is the notice, not the phone screens.
 */

// Phones and phone-like handsets only. No iPad, no generic "Tablet".
const MOBILE_UA_PATTERN = /Android.*Mobile|iPhone|iPod|IEMobile|Opera Mini|BlackBerry|BB10|webOS|Windows Phone/i;

/**
 * True only when the current device is confidently a mobile handset.
 *
 * @returns {boolean}
 */
export function isMobileDevice() {
  if (typeof navigator === 'undefined') {
    return false;
  }

  // PRIMARY: UA Client Hints. A boolean from the browser about the device.
  const uaData = navigator.userAgentData;
  if (uaData && typeof uaData.mobile === 'boolean') {
    return uaData.mobile;
  }

  // FALLBACK: only where UA-CH is unavailable (Safari, Firefox).
  const ua = navigator.userAgent;
  if (typeof ua === 'string' && ua.length > 0) {
    return MOBILE_UA_PATTERN.test(ua);
  }

  return false;
}

export default isMobileDevice;
